import { useTranslation } from 'react-i18next'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { Plus } from 'lucide-react'
import type { AddToProductImage, AddToProductTarget, DropTargetData } from '../types'
import { SlotNumberBadge } from './slot/SlotNumberBadge'
import { SlotDirtyDot } from './slot/SlotDirtyDot'
import { SlotDeleteButton } from './slot/SlotDeleteButton'
import type { MediaSize } from './MediaSizeToggle'
import { IMAGE_SIZES } from './mediaConstants'

/**
 * SlotFill represents the visual state of a single channel slot for the
 * AddToProductSlotGrid. `linkId` is required for EXISTING slots so the slot
 * can act as a same-channel drag SOURCE (port of the assets-tab DnD pattern).
 */
export interface SlotFill {
  url: string
  isPending?: boolean
  /** product_asset_link id — present for persisted slots, absent for pending. */
  linkId?: string
  /** Asset id (used by the source-image overlay during slot-to-slot drag). */
  assetId?: string
}

interface AddToProductSlotGridProps {
  filled: Record<number, SlotFill>
  channelId: string
  size: MediaSize
  onDelete?: (target: AddToProductTarget) => void
}

interface SlotProps {
  n: number
  channelId: string
  filled?: SlotFill
  onDelete?: (target: AddToProductTarget) => void
}

interface DraggableImageProps {
  fill: SlotFill
  n: number
  channelId: string
}

/**
 * The image surface inside an EXISTING slot — wrapped in useDraggable so the
 * user can move it to another slot in the same channel (same-channel reorder
 * via the reorderPlan). Pending slots are NOT draggable (no linkId, must be
 * saved first). The delete button sits outside this draggable wrapper so its
 * clicks are not intercepted by the drag listeners.
 */
function DraggableSlotImage({ fill, n, channelId }: DraggableImageProps) {
  const dragId = `slotimg-${channelId}-${n}`
  const image: AddToProductImage | undefined = fill.assetId
    ? { id: fill.assetId, url: fill.url, kind: 'asset' }
    : undefined
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: dragId,
    disabled: !fill.linkId || fill.isPending,
    data: {
      kind: 'slot-image' as const,
      channelId,
      slot: n,
      linkId: fill.linkId,
      image,
    },
  })
  const style: React.CSSProperties = {
    opacity: isDragging ? 0.35 : 1,
    touchAction: 'none',
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`atp-slotdrag-${channelId}-${n}`}
      data-draggable={!fill.linkId || fill.isPending ? 'false' : 'true'}
      className={
        !fill.linkId || fill.isPending
          ? 'absolute inset-0'
          : 'absolute inset-0 cursor-grab active:cursor-grabbing'
      }
      {...attributes}
      {...listeners}
    >
      <img src={fill.url} alt="" className="w-full h-full object-cover pointer-events-none" />
    </div>
  )
}

interface SlotContentProps {
  filled: SlotFill
  n: number
  channelId: string
  onDelete?: (target: AddToProductTarget) => void
}

function SlotContent({ filled, n, channelId, onDelete }: SlotContentProps) {
  const { t } = useTranslation()
  return (
    <>
      <DraggableSlotImage fill={filled} n={n} channelId={channelId} />
      <SlotNumberBadge n={n} />
      {filled.isPending && <SlotDirtyDot />}
      {filled.isPending && (
        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 text-3xs font-semibold uppercase tracking-wide rounded bg-amber-500 text-black z-10">
          {t('addToProduct.media.unsavedBadge')}
        </span>
      )}
      {onDelete && (
        <SlotDeleteButton
          target={{ type: 'channel', channelId, slot: n }}
          onDelete={onDelete}
        />
      )}
    </>
  )
}

function EmptySlot({ n }: { n: number }) {
  const { t } = useTranslation()
  return (
    <div className="text-neutral-400 text-center">
      <Plus className="w-5 h-5 mx-auto mb-1" />
      <div>{t('addToProduct.media.slotLabel', { n })}</div>
    </div>
  )
}

function ringClass(isPending: boolean, isOver: boolean): string {
  if (isPending) return 'border-amber-500 ring-2 ring-amber-300/40'
  if (isOver) return 'border-orange-500 bg-orange-50 dark:bg-orange-950/30'
  return 'border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900'
}

function Slot({ n, channelId, filled, onDelete }: SlotProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `chan-${channelId}-slot-${n}`,
    data: { target: 'channel', channelId, slot: n } satisfies DropTargetData,
  })
  const ring = ringClass(Boolean(filled?.isPending), isOver)
  return (
    <div
      ref={setNodeRef}
      data-testid={`atp-slot-${channelId}-${n}`}
      data-slot={n}
      data-filled={Boolean(filled)}
      data-pending={filled?.isPending ? 'true' : undefined}
      className={`group aspect-square rounded-xl overflow-hidden relative border-2 border-dashed flex items-center justify-center text-xs ${ring}`}
    >
      {filled ? (
        <SlotContent filled={filled} n={n} channelId={channelId} onDelete={onDelete} />
      ) : (
        <EmptySlot n={n} />
      )}
    </div>
  )
}

/**
 * Droppable channel slot grid. Slot count is derived from `filled`: the highest
 * occupied slot number plus one trailing empty slot. An empty `filled` map
 * still yields exactly one empty slot. Drag-end logic stays in the central
 * handler (drop targets carry `{ target:'channel', channelId, slot }`).
 *
 * Filled slots also act as DRAG SOURCES (slot-to-slot reorder) — the
 * draggable wrapper carries `{ kind:'slot-image', channelId, slot, linkId }`
 * which the workspace's handleDragEnd uses to call `pending.reorderSlots()`.
 *
 * `size` controls the grid track sizing via inline minmax(px,1fr).
 */
export function AddToProductSlotGrid({
  filled,
  channelId,
  size,
  onDelete,
}: AddToProductSlotGridProps) {
  const occupied = Object.keys(filled)
    .map(Number)
    .filter((n) => Number.isFinite(n) && n >= 1)
  const max = occupied.length ? Math.max(...occupied) : 0
  const slots = Array.from({ length: max + 1 }, (_, i) => i + 1)
  const minPx = IMAGE_SIZES[size]
  return (
    <div
      data-size={size}
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minPx}px, 1fr))` }}
    >
      {slots.map((n) => (
        <Slot
          key={n}
          n={n}
          channelId={channelId}
          filled={filled[n]}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}
